import { DataTable } from '@/components/ui/data-table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useEventRaidReports,
  useTeamEvent,
  useTeamEventRaidReportsMap,
} from 'app/frontend/base/config/queries'
import { useMemo, useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import dayjs from 'dayjs'
import EventRaidReportsDialog from '../../TeamEvent/components/EventRaidReportsDialog'
import {
  eventDateColumn,
  eventRaidReportsColumn,
  eventTimeColumn,
  eventTitleColumn,
} from '../../column-definitions/events'
import { useTeam } from 'app/frontend/base/config/context/team'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  events: wowaudit.EventBasicReference[]
  isLoading: boolean
}

const EventInsightsEventsDialog = ({
  open,
  onOpenChange,
  events,
  isLoading,
}: Props) => {
  const { team } = useTeam()
  const [raidReportsDialogEventId, setRaidReportsDialogEventId] = useState<
    number | undefined
  >(undefined)
  const [raidReportsDialogOpen, setRaidReportsDialogOpen] = useState(false)
  const { data: raidReportsMapResponse, isLoading: isRaidReportsMapLoading } =
    useTeamEventRaidReportsMap(team.id, open)
  const raidReportsByEventId =
    raidReportsMapResponse?.raidReportsByEventId || {}
  const {
    data: raidReportsDialogEvent,
    isLoading: isRaidReportsDialogEventLoading,
  } = useTeamEvent(team.id, raidReportsDialogEventId)
  const { data: raidReports = [], isLoading: isRaidReportsLoading } =
    useEventRaidReports(
      team.id,
      raidReportsDialogEventId,
      raidReportsDialogOpen,
    )

  const sortedEvents = useMemo(() => {
    const eventTimestamp = (event: wowaudit.EventBasicReference) =>
      dayjs(event.startsAt).valueOf()

    return [...events].sort((a, b) => eventTimestamp(b) - eventTimestamp(a))
  }, [events])

  const columns = useMemo<ColumnDef<wowaudit.EventBasicReference>[]>(
    () => [
      eventTitleColumn<wowaudit.EventBasicReference>(team),
      eventDateColumn<wowaudit.EventBasicReference>(),
      eventTimeColumn<wowaudit.EventBasicReference>(),
      eventRaidReportsColumn<wowaudit.EventBasicReference>({
        raidReportsByEventId,
        isLoading: isRaidReportsMapLoading,
        onOpen: (eventId) => {
          setRaidReportsDialogEventId(eventId)
          setRaidReportsDialogOpen(true)
        },
      }),
    ],
    [isRaidReportsMapLoading, raidReportsByEventId],
  )

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className='max-w-4xl'
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Past events in scope</DialogTitle>
          </DialogHeader>
          <DataTable<wowaudit.EventBasicReference, unknown>
            className='whitespace-nowrap'
            columns={columns as ColumnDef<wowaudit.EventBasicReference>[]}
            data={sortedEvents}
            includeHeader={true}
            pagination={10}
            emptyMessage={() =>
              isLoading ? 'Loading events...' : 'No events found'
            }
          />
        </DialogContent>
      </Dialog>
      <EventRaidReportsDialog
        open={raidReportsDialogOpen}
        onOpenChange={(nextOpen) => {
          setRaidReportsDialogOpen(nextOpen)
          if (!nextOpen) {
            setRaidReportsDialogEventId(undefined)
          }
        }}
        reports={raidReports}
        event={raidReportsDialogEvent}
        isLoading={isRaidReportsLoading || isRaidReportsDialogEventLoading}
      />
    </>
  )
}

export default EventInsightsEventsDialog
